import React from 'react';
import { Sparkles, Shield, Compass, Crown, Heart, Clock, AlertTriangle, TrendingUp, Check, Route, Zap } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Selector visual de "Estrategia de Portafolio" — humano, no competitivo.
 *
 * Valores válidos: 'competente_integral' | 'ruta_inteligente' | 'mixta' | 'destacado_focalizado' | 'alto_rendimiento' | null
 *
 * Props:
 *   - value: string | null
 *   - onChange: (value: string) => void
 *   - compacto: bool (oculta los bullets explicativos para vistas embebidas)
 */

const ESTRATEGIAS = [
    {
        id: 'competente_integral',
        titulo: 'Competente Integral',
        subtitulo: 'Estabilidad y equilibrio',
        icon: Shield,
        objetivo: 'Mantener un desempeño consistente y equilibrado en TODOS los indicadores.',
        perfil: [
            'Docentes que buscan estabilidad y orden',
            'Quieres minimizar riesgos',
            'Necesitas optimizar tiempo y desgaste emocional',
        ],
        metricas: {
            dificultad: 2,
            cargaTiempo: 2,
            riesgoSobreExigencia: 1,
            impactoResultado: 3,
        },
        nota: 'Una estrategia sólida y muy humana. Apuntas a Competente parejo, sin sobreexigirte.',
        tono: {
            border: 'border-teal-300',
            ring: 'ring-teal-400',
            bg: 'bg-teal-50/70',
            iconBg: 'bg-teal-100',
            iconColor: 'text-teal-700',
            chip: 'bg-teal-100 text-teal-900',
            hover: 'hover:border-teal-300',
        },
    },
    {
        id: 'ruta_inteligente',
        titulo: 'Ruta Inteligente',
        subtitulo: 'Lo esencial, sostenible y sin desgaste',
        icon: Route,
        objetivo: 'Recorrer el Portafolio por el camino más eficiente: cubrir lo esencial sin sobreexponerte.',
        perfil: [
            'Tienes tiempo o energía limitada',
            'Quieres un Portafolio coherente y sostenible',
            'Prefieres simplicidad antes que profundidad innecesaria',
        ],
        metricas: {
            dificultad: 1,
            cargaTiempo: 1,
            riesgoSobreExigencia: 1,
            impactoResultado: 3,
        },
        nota: 'Una ruta protectora del bienestar docente. Foco en Obligatorios + blindaje contra Insuficientes.',
        tono: {
            border: 'border-emerald-300',
            ring: 'ring-emerald-400',
            bg: 'bg-emerald-50/70',
            iconBg: 'bg-emerald-100',
            iconColor: 'text-emerald-700',
            chip: 'bg-emerald-100 text-emerald-900',
            hover: 'hover:border-emerald-300',
        },
    },
    {
        id: 'mixta',
        titulo: 'Estrategia Mixta',
        subtitulo: 'Profundidad selectiva + Competente sólido',
        badgeRecomendada: 'Recomendación Acontista Educa',
        icon: Compass,
        recomendada: true,
        objetivo: 'Buscar Destacado solo en los indicadores estratégicos. Competente sólido en el resto.',
        perfil: [
            'Balance entre profundidad y sostenibilidad',
            'Maximiza la probabilidad de buen resultado global',
            'Reserva tu energía para 3–5 indicadores clave',
        ],
        metricas: {
            dificultad: 3,
            cargaTiempo: 3,
            riesgoSobreExigencia: 2,
            impactoResultado: 5,
        },
        nota: 'Estrategia oficialmente recomendada por Acontista Educa. Basada en sostenibilidad docente, análisis de datos reales, carga horaria razonable y maximización del resultado global.',
        tono: {
            border: 'border-rose-400',
            ring: 'ring-rose-400',
            bg: 'bg-gradient-to-br from-rose-50/80 via-white to-amber-50/40',
            iconBg: 'bg-rose-100',
            iconColor: 'text-rose-700',
            chip: 'bg-rose-100 text-rose-900',
            hover: 'hover:border-rose-300',
        },
    },
    {
        id: 'destacado_focalizado',
        titulo: 'Destacado Focalizado',
        subtitulo: 'Profundidad en tus fortalezas',
        icon: Crown,
        objetivo: 'Apuntar a Destacado en indicadores donde tu fortaleza profesional es más visible.',
        perfil: [
            'Tienes experiencia previa en el Portafolio',
            'Alta capacidad reflexiva y analítica',
            'Tienes más tiempo disponible que el promedio',
        ],
        metricas: {
            dificultad: 5,
            cargaTiempo: 5,
            riesgoSobreExigencia: 4,
            impactoResultado: 4,
        },
        nota: 'Exige rigurosidad y tiempo. Si la coherencia global o tu energía se ven afectadas, considera la Estrategia Mixta.',
        tono: {
            border: 'border-violet-300',
            ring: 'ring-violet-400',
            bg: 'bg-violet-50/70',
            iconBg: 'bg-violet-100',
            iconColor: 'text-violet-700',
            chip: 'bg-violet-100 text-violet-900',
            hover: 'hover:border-violet-300',
        },
    },
    {
        id: 'alto_rendimiento',
        titulo: 'Alto Rendimiento',
        subtitulo: 'Máxima exigencia · solo si tienes condiciones reales',
        icon: Zap,
        objetivo: 'Maximizar la cantidad de indicadores en nivel Destacado en todo el Portafolio.',
        perfil: [
            'Amplia experiencia previa en evaluación docente',
            'Evidencia abundante y redes de colaboración consolidadas',
            'Muchas horas semanales realmente disponibles, sostenidas',
        ],
        metricas: {
            dificultad: 5,
            cargaTiempo: 5,
            riesgoSobreExigencia: 5,
            impactoResultado: 5,
        },
        advertenciaFuerte: 'Alta carga de trabajo · alto desgaste esperado · mayor inversión de tiempo. La plataforma promueve estrategia inteligente y sostenibilidad docente, no maximización extrema.',
        nota: 'Si aparecen señales de cansancio o falta de tiempo, te sugeriremos cambiar a Estrategia Mixta o Ruta Inteligente sin culpa. Tu bienestar es parte del trabajo profesional.',
        tono: {
            border: 'border-orange-400',
            ring: 'ring-orange-400',
            bg: 'bg-orange-50/70',
            iconBg: 'bg-orange-100',
            iconColor: 'text-orange-700',
            chip: 'bg-orange-100 text-orange-900',
            hover: 'hover:border-orange-300',
        },
    },
];

const METRICA_LABELS = {
    dificultad: { label: 'Dificultad', icon: AlertTriangle, color: 'bg-orange-400' },
    cargaTiempo: { label: 'Carga de tiempo', icon: Clock, color: 'bg-amber-400' },
    riesgoSobreExigencia: { label: 'Riesgo de sobreexigencia', icon: Heart, color: 'bg-rose-400' },
    impactoResultado: { label: 'Impacto en resultado global', icon: TrendingUp, color: 'bg-emerald-500' },
};

function Bars({ valor }) {
    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
                <span
                    key={i}
                    className={cn(
                        'h-1.5 w-3 rounded-full transition-colors',
                        i <= valor ? 'bg-slate-700' : 'bg-slate-200'
                    )}
                />
            ))}
        </div>
    );
}

export default function EstrategiaPortafolioSelector({ value, onChange, compacto = false }) {
    const seleccionada = value || null;

    return (
        <div className="space-y-5" data-testid="estrategia-portafolio-selector">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/80 to-rose-50/40 p-5">
                <Sparkles className="mt-1 h-5 w-5 shrink-0 text-amber-700" />
                <div className="space-y-1.5">
                    <p className="text-sm font-bold text-slate-900 leading-snug">
                        ¿Cuál es tu estrategia de Portafolio?
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        Esta elección guía cómo el sistema conversa contigo: ajusta su profundidad, su complejidad, el tiempo que te sugiere invertir y la prioridad de los indicadores.
                        Puedes cambiarla cuando quieras. No hay opciones correctas o incorrectas, hay estrategias que se ajustan mejor a tu realidad docente.
                    </p>
                    <p className="mt-2 rounded-lg bg-white/70 border border-amber-200/70 px-3 py-2 text-xs italic text-slate-700 leading-relaxed">
                        "Un buen Portafolio no es el que busca perfección absoluta, sino el que logra coherencia pedagógica, sostenibilidad y evidencia sólida."
                    </p>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" role="radiogroup" aria-label="Estrategia de Portafolio">
                {ESTRATEGIAS.map((e) => {
                    const Icon = e.icon;
                    const activa = seleccionada === e.id;
                    return (
                        <button
                            key={e.id}
                            type="button"
                            role="radio"
                            aria-checked={activa}
                            onClick={() => onChange(e.id)}
                            className={cn(
                                'group relative flex h-full flex-col items-start gap-4 rounded-2xl border-2 p-5 text-left transition-all duration-200',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                                activa
                                    ? cn(e.tono.border, e.tono.bg, 'shadow-md ring-2', e.tono.ring)
                                    : cn('border-slate-200 bg-white', e.tono.hover, 'hover:shadow-sm hover:-translate-y-0.5')
                            )}
                            data-testid={`estrategia-card-${e.id}`}
                        >
                            {e.recomendada && (
                                <span
                                    className="absolute -top-3 left-4 flex items-center gap-1 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md"
                                    data-testid="estrategia-recomendada-tag"
                                >
                                    <Sparkles className="h-3 w-3" />
                                    {e.badgeRecomendada || 'Recomendación Acontista Educa'}
                                </span>
                            )}

                            <div className="flex w-full items-start justify-between">
                                <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl', e.tono.iconBg)}>
                                    <Icon className={cn('h-5 w-5', e.tono.iconColor)} />
                                </div>
                                {activa && (
                                    <span
                                        className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold', e.tono.chip)}
                                        data-testid={`estrategia-activa-${e.id}`}
                                    >
                                        <Check className="h-3 w-3" />
                                        Tu estrategia
                                    </span>
                                )}
                            </div>

                            <div className="space-y-1.5">
                                <h3 className="font-heading text-base font-bold text-slate-900">{e.titulo}</h3>
                                {e.badgeRecomendada && (
                                    <span
                                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                                        data-testid={`estrategia-badge-acontista-${e.id}`}
                                    >
                                        {e.badgeRecomendada}
                                    </span>
                                )}
                                <p className="text-xs font-medium text-slate-500">{e.subtitulo}</p>
                            </div>

                            <p className="text-sm text-slate-700 leading-relaxed">{e.objetivo}</p>

                            {e.advertenciaFuerte && (
                                <div
                                    className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50/80 px-3 py-2 text-xs text-orange-900 leading-relaxed"
                                    data-testid={`estrategia-advertencia-${e.id}`}
                                >
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-600" />
                                    <span><strong className="font-semibold">Atención:</strong> {e.advertenciaFuerte}</span>
                                </div>
                            )}

                            {!compacto && (
                                <ul className="space-y-1.5 w-full">
                                    {e.perfil.map((p, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                                            <span className="leading-relaxed">{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="w-full space-y-2 border-t border-slate-100 pt-3">
                                {Object.entries(e.metricas).map(([key, valor]) => {
                                    const meta = METRICA_LABELS[key];
                                    const MIcon = meta.icon;
                                    return (
                                        <div key={key} className="flex items-center justify-between gap-2">
                                            <span className="flex items-center gap-1.5 text-[11px] text-slate-600">
                                                <MIcon className="h-3 w-3 text-slate-400" />
                                                {meta.label}
                                            </span>
                                            <Bars valor={valor} />
                                        </div>
                                    );
                                })}
                            </div>

                            <p className="mt-auto pt-2 text-xs italic text-slate-500 leading-relaxed border-t border-slate-100/80 w-full">
                                {e.nota}
                            </p>
                        </button>
                    );
                })}
            </div>

            {seleccionada && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-xs text-slate-600 leading-relaxed" data-testid="estrategia-feedback">
                    El sistema ajustará desde ahora la profundidad, complejidad, tiempo recomendado y prioridad de indicadores en base a esta estrategia. Puedes cambiarla en cualquier momento.
                </div>
            )}
        </div>
    );
}

export const ESTRATEGIA_LABELS = {
    competente_integral: 'Competente Integral',
    ruta_inteligente: 'Ruta Inteligente',
    mixta: 'Estrategia Mixta',
    destacado_focalizado: 'Destacado Focalizado',
    alto_rendimiento: 'Alto Rendimiento',
};

// Etiquetas extendidas: incluyen el sello pedagógico "Recomendación Acontista Educa"
// donde corresponde (sólo Mixta). Úsalas en títulos grandes, headers de bloque,
// resúmenes y textos donde quieras que el sello sea visible.
export const ESTRATEGIA_LABELS_FULL = {
    competente_integral: 'Competente Integral',
    ruta_inteligente: 'Ruta Inteligente',
    mixta: 'Estrategia Mixta — Recomendación Acontista Educa',
    destacado_focalizado: 'Destacado Focalizado',
    alto_rendimiento: 'Alto Rendimiento',
};

// Subtítulo / badge pequeño para mostrar bajo el nombre principal.
// null cuando la estrategia no lleva sello.
export const ESTRATEGIA_BADGES = {
    competente_integral: null,
    ruta_inteligente: null,
    mixta: 'Recomendación Acontista Educa',
    destacado_focalizado: null,
    alto_rendimiento: null,
};
